import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Search, X } from "lucide-react";
import Swal from "sweetalert2";
import EditarResponsavelModal from "../../components/EditarResponsavelModal/EditarResponsavelModal";
import ImportarAlunos from "../../components/ImportarAlunos/ImportarAlunos";
import { useUsuariosInstituicao } from "../../hooks/useUsuariosInstituicao";
import { useCoordenadoresDaCoordenacao } from "../../hooks/useCoordenadoresDaCoordenacao";
import { getUsuarioLogadoAdaptado, idsIguais } from "../../utils/ids";
import {
  criarTurma,
  vincularResponsavel,
  vincularAluno,
  editarCargoContatoResponsavelTurma,
  TipoTurma,
} from "../../api/turmas";
import "./CriarTurma.scss";

const STORAGE_COORDENACAO_ATUAL = "coordenacaoAtualId";

const CARGO_COORDENADOR_PADRAO = "Coordenador";
const PERMS_TOTAIS = { eventos: true, responsaveis: true, alunos: true };
const PERMS_DEFAULT = { eventos: true, responsaveis: true, alunos: true };

function getCoordenacaoAtualId() {
  return localStorage.getItem(STORAGE_COORDENACAO_ATUAL) || null;
}

// Monta responsável "fixo" a partir de um coordenador da coordenação.
// Spec: cargo default = "Coordenador" (editável); contato vazio; perms totais; fixo=true.
function fixoDoCoordenador(coord) {
  return {
    userId: coord.id,
    nome: coord.nome,
    cargo: CARGO_COORDENADOR_PADRAO,
    contato: "",
    permissoes: { ...PERMS_TOTAIS },
    fixo: true,
    coordenadorPadrao: true,
    removivel: false,
  };
}

export default function CriarTurma() {
  const navigate = useNavigate();

  const [nome, setNome] = useState("");
  const [complemento, setComplemento] = useState("");
  const [tipo, setTipo] = useState("primaria");

  const [responsaveisManuais, setResponsaveisManuais] = useState([]);
  // Map userId → { cargo, contato } com edições locais aos fixos. Aplicado
  // via PUT cargo-contato após criar a turma (sync já criou os vínculos).
  const [fixoOverrides, setFixoOverrides] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editIndex, setEditIndex] = useState(null);

  const [coordenacaoAtualId, setCoordenacaoAtualId] = useState(getCoordenacaoAtualId);

  const [buscaAluno, setBuscaAluno] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [alunoSelecionadoId, setAlunoSelecionadoId] = useState(null);
  const [alunosSelecionados, setAlunosSelecionados] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const searchWrapRef = useRef(null);

  const usuarioLogado = useMemo(() => getUsuarioLogadoAdaptado(), []);
  const instituicaoIdLogado = usuarioLogado?.instituicaoId || usuarioLogado?.faculdadeId || null;

  const { usuarios: baseUsuarios } = useUsuariosInstituicao(instituicaoIdLogado);
  const { coordenadores } = useCoordenadoresDaCoordenacao(coordenacaoAtualId);

  // Listener: troca de coordenação no menu lateral → refetch
  useEffect(() => {
    const handler = () => setCoordenacaoAtualId(getCoordenacaoAtualId());
    window.addEventListener("coordenacaoAtual:changed", handler);
    return () => window.removeEventListener("coordenacaoAtual:changed", handler);
  }, []);

  // Fechar dropdown de busca ao clicar fora
  useEffect(() => {
    const onDown = (e) => {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Sincroniza responsáveis manuais: se um deles for adicionado depois como
  // coordenador (sync de cima), removemos o manual para não duplicar.
  useEffect(() => {
    const idsCoords = new Set(coordenadores.map((c) => String(c.id)));
    setResponsaveisManuais((prev) =>
      prev.filter((r) => !idsCoords.has(String(r.userId))),
    );
  }, [coordenadores]);

  const responsaveisFixos = useMemo(
    () =>
      coordenadores.map((c) => {
        const base = fixoDoCoordenador(c);
        const ov = fixoOverrides[String(c.id)];
        return ov ? { ...base, cargo: ov.cargo, contato: ov.contato } : base;
      }),
    [coordenadores, fixoOverrides],
  );

  const responsaveisAll = useMemo(
    () => [...responsaveisFixos, ...responsaveisManuais],
    [responsaveisFixos, responsaveisManuais],
  );

  const candidatosBusca = useMemo(() => {
    const q = buscaAluno.trim().toLowerCase();
    if (!q) return [];
    return baseUsuarios
      .filter((u) => u.tipo === "Aluno" || u.tipo === "aluno")
      .filter(
        (u) =>
          u.nome?.toLowerCase().includes(q) ||
          String(u.user || "").toLowerCase().includes(q) ||
          String(u.email || "").toLowerCase().includes(q),
      )
      .filter((u) => !alunosSelecionados.some((a) => idsIguais(a.id, u.id)))
      .slice(0, 8)
      .map((u) => ({
        id: u.id,
        nome: u.nome,
        matricula: u.user,
        email: u.email || "",
      }));
  }, [buscaAluno, baseUsuarios, alunosSelecionados]);

  const usuariosComUser = useMemo(
    () =>
      baseUsuarios.map((u) => ({
        ...u,
        user: u.user ?? String(u.id),
      })),
    [baseUsuarios],
  );

  // Busca de "adicionar responsável" só lista Responsável e Aluno (coords
  // entram automáticos pela coordenação; coords de outras coordenações não).
  const usuariosParaResponsavel = useMemo(
    () =>
      usuariosComUser.filter((u) => {
        const t = String(u.tipo || "").toLowerCase();
        return t === "responsavel" || t === "aluno";
      }),
    [usuariosComUser],
  );

  const openAdicionarResponsavel = () => {
    setModalMode("add");
    setEditIndex(null);
    setModalOpen(true);
  };

  const openEditarResponsavel = (idxInAll) => {
    setModalMode("edit");
    setEditIndex(idxInAll);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditIndex(null);
  };

  const handleSaveResponsavel = (payload) => {
    const cargoNormalizado = String(payload.cargo ?? "").trim();
    // Modal devolve userId = login/email; resolve para o Guid real do usuário.
    const usuarioReal = usuariosComUser.find(
      (u) => String(u.user) === String(payload.userId),
    );
    const normalized = {
      userId: usuarioReal?.id ?? payload.userId ?? "",
      nome: payload.nome ?? "",
      cargo: cargoNormalizado,
      contato: payload.contato ?? "",
      permissoes: { ...PERMS_DEFAULT, ...(payload.permissoes || {}) },
    };

    if (modalMode === "add") {
      if (responsaveisAll.some((r) => String(r.userId) === String(normalized.userId))) {
        Swal.fire({
          icon: "warning",
          title: "Já adicionado",
          text: "Esse usuário já é responsável da turma.",
        });
        return;
      }
      setResponsaveisManuais((prev) => [...prev, normalized]);
      closeModal();
      return;
    }

    const idxManual = editIndex - responsaveisFixos.length;
    if (idxManual < 0) {
      // Fixo: cargo/contato editáveis (userId/perms travados pelo modal).
      // Guarda em overrides; aplicado via PUT após criar turma.
      const fixoAtual = responsaveisFixos[editIndex];
      if (!fixoAtual) {
        closeModal();
        return;
      }
      setFixoOverrides((prev) => ({
        ...prev,
        [String(fixoAtual.userId)]: {
          cargo: normalized.cargo,
          contato: normalized.contato,
        },
      }));
      closeModal();
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Cargo/contato atualizado — salvo ao criar a turma",
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true,
      });
      return;
    }
    setResponsaveisManuais((prev) => {
      const next = [...prev];
      next[idxManual] = { ...next[idxManual], ...normalized };
      return next;
    });
    closeModal();
  };

  const handleRemoveResponsavel = () => {
    if (editIndex === null) return;
    const idxManual = editIndex - responsaveisFixos.length;
    if (idxManual < 0) {
      Swal.fire({
        icon: "warning",
        title: "Não removível",
        text: "Coordenadores da coordenação são fixos. Remova-o pela coordenação para sumir aqui.",
      });
      return;
    }
    setResponsaveisManuais((prev) => prev.filter((_, i) => i !== idxManual));
    closeModal();
  };

  const handleAdicionarAluno = () => {
    if (!alunoSelecionadoId) {
      Swal.fire({ icon: "warning", title: "Selecione um aluno" });
      return;
    }
    const aluno = baseUsuarios.find((u) => idsIguais(u.id, alunoSelecionadoId));
    if (!aluno) return;
    setAlunosSelecionados((prev) => [
      ...prev,
      { id: aluno.id, nome: aluno.nome, matricula: aluno.user, email: aluno.email || "" },
    ]);
    setBuscaAluno("");
    setAlunoSelecionadoId(null);
    setDropdownOpen(false);
  };

  const handleRemoverAluno = (id) => {
    setAlunosSelecionados((prev) => prev.filter((a) => !idsIguais(a.id, id)));
  };

  const limparFormulario = () => {
    setNome("");
    setComplemento("");
    setTipo("primaria");
    setResponsaveisManuais([]);
    setFixoOverrides({});
    setBuscaAluno("");
    setDropdownOpen(false);
    setAlunoSelecionadoId(null);
    setAlunosSelecionados([]);
  };

  const handleCriarTurma = async () => {
    if (!nome.trim()) {
      Swal.fire({ icon: "warning", title: "Informe o nome" });
      return;
    }
    if (!coordenacaoAtualId) {
      Swal.fire({
        icon: "error",
        title: "Sem coordenação",
        text: "Selecione uma coordenação no menu lateral antes de criar a turma.",
      });
      return;
    }

    setSalvando(true);
    try {
      const tipoEnum = tipo === "primaria" ? TipoTurma.Primario : TipoTurma.Secundario;
      const { id: turmaId } = await criarTurma({
        nome: nome.trim(),
        tipo: tipoEnum,
        instituicaoId: instituicaoIdLogado,
        complemento: complemento.trim() || null,
        coordenacaoId: coordenacaoAtualId,
      });

      // Coordenadores fixos já entram no backend pelo sync. Aqui aplicamos
      // edits de cargo/contato dos fixos via PUT, depois vinculamos manuais
      // e alunos.
      for (const fixo of responsaveisFixos) {
        const ov = fixoOverrides[String(fixo.userId)];
        if (!ov) continue;
        await editarCargoContatoResponsavelTurma(turmaId, fixo.userId, {
          cargo: ov.cargo || null,
          contato: ov.contato || null,
        });
      }

      for (const r of responsaveisManuais) {
        await vincularResponsavel(turmaId, {
          turmaId,
          userId: r.userId,
          cargo: r.cargo || null,
          contato: r.contato || null,
          podeCriarEditarEventos: !!r.permissoes?.eventos,
          podeEditarListaAlunos: !!r.permissoes?.alunos,
          podeGerenciarResponsaveis: !!r.permissoes?.responsaveis,
        });
      }

      for (const a of alunosSelecionados) {
        await vincularAluno(turmaId, a.id);
      }

      await Swal.fire({
        icon: "success",
        title: "Turma criada",
        timer: 1400,
        showConfirmButton: false,
      });
      limparFormulario();
      window.dispatchEvent(new Event("turmas:changed"));
      navigate("/dashboard");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erro ao criar turma",
        text: err?.message || "Tente novamente.",
      });
    } finally {
      setSalvando(false);
    }
  };

  const podeCriarTurma =
    nome.trim() && responsaveisAll.length > 0 && alunosSelecionados.length > 0 && !salvando;

  return (
    <div className="painel-evento criar-turma-page">
      <div className="eventos-wrap">
        <section className="criar-turma-card criar-turma-card--top criar-turma-card--coordenador">
          <h1 className="page-title">Criar nova turma</h1>
          <h2 className="section-title">Informações da Turma</h2>

          <div className="grid-2">
            <div className="field">
              <label>Nome</label>
              <input value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="field">
              <label>Complemento</label>
              <input
                value={complemento}
                onChange={(e) => setComplemento(e.target.value)}
                placeholder="Ex: Turma 8"
              />
            </div>
          </div>

          <div className="tipo-area">
            <div className="tipo-label">Selecione o tipo</div>
            <div className="radio-group">
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipoTurma"
                  checked={tipo === "primaria"}
                  onChange={() => setTipo("primaria")}
                />
                <span>Primária</span>
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipoTurma"
                  checked={tipo === "secundaria"}
                  onChange={() => setTipo("secundaria")}
                />
                <span>Secundária</span>
              </label>
            </div>
          </div>
        </section>

        <section className="criar-turma-card">
          <h2 className="section-title">Responsáveis da turma</h2>

          <div className="responsaveis">
            {responsaveisAll.length > 0 ? (
              responsaveisAll.map((r, idx) => (
                <div className="responsavel-card" key={`${r.userId}-${idx}`}>
                  <div className="responsavel-top">
                    <span className="chip">{r.nome || "Responsável"}</span>
                    <button
                      type="button"
                      className="btn-edit"
                      onClick={() => openEditarResponsavel(idx)}
                    >
                      <Pencil size={12} />
                      <span className="btn-edit__text">Editar</span>
                    </button>
                  </div>
                  <div className="grid-2 inner">
                    <div className="field">
                      <label>Cargo</label>
                      <input value={r.cargo || ""} readOnly />
                    </div>
                    <div className="field">
                      <label>Contato</label>
                      <input value={r.contato || ""} readOnly />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <p>Selecione uma coordenação para ver os coordenadores fixos.</p>
              </div>
            )}

            <button
              type="button"
              className="btn-primary wide add-responsavel-btn"
              onClick={openAdicionarResponsavel}
            >
              Adicionar Responsável
            </button>
          </div>
        </section>

        <section className="criar-turma-card alunos-card">
          <div className="section-head">
            <h2 className="section-title">Lista de alunos</h2>
            <ImportarAlunos
              usuarios={baseUsuarios}
              faculdadeId={instituicaoIdLogado}
              idsJaExistentes={alunosSelecionados.map((a) => a.id)}
              buttonLabel="Importar lista"
              className="btn-import"
              onImportConfirm={(idsNovos) => {
                const novos = baseUsuarios
                  .filter((u) => idsNovos.some((id) => idsIguais(id, u.id)))
                  .map((u) => ({
                    id: u.id,
                    nome: u.nome,
                    matricula: u.user,
                    email: u.email || "",
                  }));
                setAlunosSelecionados((prev) => {
                  const atuais = new Set(prev.map((a) => String(a.id)));
                  return [...prev, ...novos.filter((n) => !atuais.has(String(n.id)))];
                });
              }}
            />
          </div>

          <div className="search-wrap" ref={searchWrapRef}>
            <Search size={15} className="search-ico" />
            <input
              value={buscaAluno}
              onChange={(e) => {
                setBuscaAluno(e.target.value);
                setAlunoSelecionadoId(null);
                setDropdownOpen(true);
              }}
              onFocus={() => candidatosBusca.length > 0 && setDropdownOpen(true)}
              className="search-input"
              placeholder="Busque por nome, matrícula, e-mail..."
            />
            {dropdownOpen && candidatosBusca.length > 0 && (
              <div className="search-dropdown">
                {candidatosBusca.map((c) => (
                  <button
                    key={String(c.id)}
                    type="button"
                    className={`dropdown-item ${
                      idsIguais(alunoSelecionadoId, c.id) ? "active" : ""
                    }`}
                    onClick={() => {
                      setAlunoSelecionadoId(c.id);
                      setBuscaAluno(`${c.nome} (${c.matricula || ""})`);
                      setDropdownOpen(false);
                    }}
                  >
                    <div className="d-name">{c.nome}</div>
                    <div className="d-sub">{c.matricula || c.email || ""}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {alunoSelecionadoId && (
            <div className="add-aluno-row">
              <button
                type="button"
                className="btn-primary add-aluno-btn"
                onClick={handleAdicionarAluno}
              >
                Adicionar aluno
              </button>
            </div>
          )}

          {alunosSelecionados.length > 0 ? (
            <div className="alunos-list">
              {alunosSelecionados.map((a) => (
                <div className="aluno-row" key={a.id}>
                  <div className="aluno-info">
                    <div className="aluno-nome">{a.nome}</div>
                    <div className="aluno-matricula">{a.matricula || a.email || ""}</div>
                  </div>
                  <button
                    type="button"
                    className="btn-x"
                    onClick={() => handleRemoverAluno(a.id)}
                    aria-label="Remover aluno"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Nenhum aluno adicionado ainda.</p>
            </div>
          )}

          <div className="footer-action">
            <button
              type="button"
              className={`btn-create ${podeCriarTurma ? "is-ready" : ""}`}
              onClick={handleCriarTurma}
              disabled={!podeCriarTurma}
            >
              {salvando ? "Criando..." : "Criar turma"}
            </button>
          </div>
        </section>

        <EditarResponsavelModal
          usuarioLogado={usuarioLogado}
          open={modalOpen}
          onClose={closeModal}
          usuarios={usuariosParaResponsavel}
          initialValue={
            modalMode === "edit" && editIndex !== null
              ? responsaveisAll[editIndex]
              : null
          }
          onSave={handleSaveResponsavel}
          onRemove={
            modalMode === "edit" &&
            editIndex !== null &&
            !responsaveisAll[editIndex]?.fixo
              ? handleRemoveResponsavel
              : null
          }
          bloquearResponsavel={
            modalMode === "edit" &&
            editIndex !== null &&
            !!responsaveisAll[editIndex]?.fixo
          }
          bloquearRemocao={
            modalMode === "edit" &&
            editIndex !== null &&
            !!responsaveisAll[editIndex]?.fixo
          }
        />
      </div>
    </div>
  );
}
